import React from 'react'
import {
  DocumentPage,
  Text,
  View,
  StyleSheet,
  useStyle,
  Svg,
  Path,
} from '~/lib/pdf'
import Moment from 'moment'

export const defaultStyle = {
  xMargin: 0.4,
  yMargin: 0.4,
  size: 'Letter',
  accentColor: '#005BA2',
  lineHeightMultiplier: 1.0,
  listLineHeightMultiplier: 1.0,
  primaryFontFamily: 'Poppins',
  secondaryFontFamily: 'Poppins',
  headingFontSize: 24,
  bodyFontSize: 10,
  educationHeading: 'Education',
}

export const optionalEditorFields = {
  basicsJobTitle: true,
  basicsLocation: false,
  basicsSummary: true,
  basicsWebsite: false,
  basicsLinkedin: true,
  volunteer: false,
  workLocation: false,
  workSummary: false,
  educationCourses: false,
  educationLocation: false,
  educationGpa: false,
  educationTenure: true,
  skillProficiency: false,
}

export const Template = ({ jsonDocument, templateStyle, title }) => {
  const {
    secondaryFontFamily,
    accentColor,
    bodyFontSize,
    headingFontSize,
    lineHeightMultiplier,
    listLineHeightMultiplier,
  } = templateStyle
  const contactFontSize = (bodyFontSize * 5) / 6
  const labelFontSize = (headingFontSize * 7) / 12

  const styles = StyleSheet.create({
    headingContainer: {
      border: '0px solid #000000',
      marginBottom: '4px',
    },
    heading: {
      fontFamily: secondaryFontFamily,
      fontSize: headingFontSize,
      fontWeight: 'bold',
      color: '#000000',
      lineHeight: '30.72px',
      break: 'word',
    },
    labelSection: {
      fontSize: labelFontSize,
      fontWeight: 600,
      color: accentColor,
      marginBottom: '12px',
    },
    leftSection: {
      border: '0px solid #000000',
      flexBasis: '36%',
      flexGrow: 1,
      paddingRight: '40px',
    },
    rightSection: {
      flexBasis: '63%',
      flexGrow: 1,
    },
    workSection: {
      backgroundColor: '#f9f9f9',
      padding: '16px 13px 16px 20px',
      borderRadius: '8px',
      marginBottom: '12px',
    },
    bottomSection: {
      backgroundColor: '#f9f9f9',
      padding: '16px',
      borderRadius: '8px',
    },
    contactSection: {
      color: '#000000',
      fontFamily: secondaryFontFamily,
      fontSize: contactFontSize,
      fontWeight: 'bold',
      marginBottom: '12px',
    },
    contactItem: {
      paddingLeft: '12px',
    },
    contactItemRow: {
      flexDirection: 'row',
      alignItems: 'left',
      paddingBottom: '8px',
    },
    contactIcon: {
      width: '12px',
      height: '12px',
      paddingRight: '8px',
    },
    sectionTitle: {
      fontSize: bodyFontSize,
      fontWeight: 'bold',
      marginTop: '16px',
    },
    sidebarTitle: {
      fontSize: bodyFontSize,
      fontWeight: 'bold',
      color: accentColor,
      marginBottom: '4px',
    },
    skillsSection: {
      marginBottom: '12px',
      fontSize: contactFontSize,
      lineHeight: `${14 * listLineHeightMultiplier}px`,
    },
    primarySkillItem: {
      paddingBottom: '4px',
    },
    otherSkillItem: {
      // placeholder
    },
    summarySection: {
      marginBottom: '12px',
      maxHeight: '200px',
      fontSize: contactFontSize,
      lineHeight: `${14 * lineHeightMultiplier}px`,
    },
  })

  const stripHttp = (url) => {
    return url?.replace(/https{0,1}:\/\//, '')
  }

  const { basics, work, skills, education } = jsonDocument
  const { name, label, email, phone, profiles, summary } = basics || {}

  const linkedinProfile =
    profiles &&
    profiles.find((element) => {
      return element.network?.toLowerCase() === 'linkedin'
    })

  const linkedinProfileUrl = linkedinProfile && stripHttp(linkedinProfile.url)

  return (
    <DocumentPage templateStyle={templateStyle} title={title}>
      <View style={{ flexDirection: 'column' }}>
        <View style={{ flexDirection: 'row' }}>
          <View style={styles.leftSection}>
            <View style={styles.headingContainer}>
              {name && (
                <Text
                  style={styles.heading}
                  hyphenationCallback={(word) => [word]}
                >
                  {name}
                </Text>
              )}
            </View>
            <View style={styles.labelSection}>
              {label && <Text>{label}</Text>}
            </View>
            <View style={styles.summarySection}>
              <Text hyphenationCallback={(word) => [word]}>{summary}</Text>
            </View>
            <View style={styles.contactSection}>
              <Text style={styles.sidebarTitle}>Contact</Text>
              {email && (
                <View style={styles.contactItemRow}>
                  <EnvelopeIcon />
                  <Text style={styles.contactItem}>{email}</Text>
                </View>
              )}
              {linkedinProfileUrl && (
                <View style={styles.contactItemRow}>
                  <LinkedInIcon />
                  <Text style={styles.contactItem}>{linkedinProfileUrl}</Text>
                </View>
              )}
              {phone && (
                <View style={styles.contactItemRow}>
                  <PhoneIcon />
                  <Text style={styles.contactItem}>{phone}</Text>
                </View>
              )}
            </View>
            {skills &&
              skills.map((skill, index) => {
                return index == 0 ? (
                  <FirstSkillsSection
                    skill={skill}
                    key={skill.name}
                    styles={styles}
                  />
                ) : (
                  <OtherSkillsSection
                    skill={skill}
                    key={skill.name}
                    styles={styles}
                  />
                )
              })}
          </View>
          <View style={styles.rightSection}>
            <View style={styles.workSection}>
              {work && work.length > 0 && <WorkSection work={work} />}
            </View>
          </View>
        </View>
        <View style={styles.bottomSection} wrap={false}>
          {education && education.length > 0 && (
            <EducationSection education={education} styles={styles} />
          )}
        </View>
      </View>
    </DocumentPage>
  )
}

const OtherSkillsSection = ({ skill, styles }) => {
  return (
    <View>
      <Text style={styles.sidebarTitle}>{skill.name}</Text>
      <View style={styles.skillsSection}>
        <Text hyphenationCallback={(word) => [word]}>
          {skill?.keywords?.join(', ')}
        </Text>
      </View>
    </View>
  )
}

const FirstSkillsSection = ({ skill, styles }) => {
  return (
    <View>
      <Text style={styles.sidebarTitle}>{skill.name}</Text>
      <View style={styles.skillsSection}>
        {skill?.keywords?.map((keyword) => {
          if (!keyword) return null
          return (
            <Text key={keyword} style={styles.primarySkillItem}>
              {keyword}
            </Text>
          )
        })}
      </View>
    </View>
  )
}

const EducationSection = ({ education, styles }) => {
  const edSize = education.length
  const col1Size = Math.ceil(edSize / 2)
  const col1 = education.slice(0, col1Size)
  const col2 = education.slice(col1Size, edSize)

  const {
    templateStyle: { educationHeading },
  } = useStyle()
  return (
    <>
      <Text style={styles.sidebarTitle}>{educationHeading}</Text>
      <View style={{ display: 'flex', flexDirection: 'row' }}>
        <View style={{ flexBasis: '50%' }}>
          {col1.map((edItem) => (
            <EducationView education={edItem} key={edItem.key} />
          ))}
        </View>
        <View style={{ flexBasis: '50%' }}>
          {col2.map((edItem) => (
            <EducationView education={edItem} key={edItem.key} />
          ))}
        </View>
      </View>
    </>
  )
}

const WorkSection = ({ work }) => {
  return work.map((job, index) => <JobView job={job} key={`job-${index}`} />)
}

const JobView = ({ job }) => {
  const {
    templateStyle: { accentColor, bodyFontSize },
  } = useStyle()
  const titleFontSize = (bodyFontSize * 12) / 9

  const styles = StyleSheet.create({
    jobContainer: {
      paddingTop: '12px',
    },
    jobCompany: {
      fontSize: bodyFontSize,
      fontWeight: 'semibold',
      color: accentColor,
      flexDirection: 'row',
      marginBottom: '8px',
    },
    jobPosition: {
      fontSize: titleFontSize,
      fontWeight: 'bold',
      color: '#000000',
      marginBottom: '4px',
    },
  })
  if (!job.position && !job.name) {
    return null
  }

  const { name, startDate, position, highlights } = job || {}
  const showComma = name && startDate

  return (
    <View style={styles.jobContainer} wrap={false}>
      <Text style={styles.jobPosition}>{position}</Text>
      <View style={styles.jobCompany}>
        <Text>
          {name}
          {showComma && ', '}
          <Tenure item={job} />
        </Text>
      </View>
      {highlights &&
        highlights.map((highlight, index) => (
          <BulletLine key={`highlight-${index}`}>{highlight}</BulletLine>
        ))}
    </View>
  )
}

const EducationView = ({ education }) => {
  const {
    templateStyle: { bodyFontSize, lineHeightMultiplier },
  } = useStyle()
  const subHeadingFontSize = (bodyFontSize * 8) / 9

  const lineHeight = `${14 * lineHeightMultiplier}px`

  const styles = StyleSheet.create({
    edContainer: {
      paddingTop: '12px',
      width: 'auto',
    },
    institution: {
      fontSize: subHeadingFontSize,
      paddingRight: '4px',
      fontWeight: 'bold',
      display: 'inline',
    },
    typeArea: {
      fontSize: bodyFontSize,
      fontWeight: 'semibold',
      paddingBottom: '2px',
      lineHeight,
    },
    edTenure: {
      fontSize: subHeadingFontSize,
      color: '#000000',
      display: 'inline',
    },
  })

  if (!education.institution && !education.studyType) {
    return null
  }

  return (
    <View style={styles.edContainer} wrap={false}>
      <View style={{ flexDirection: 'column' }}>
        <Text style={styles.typeArea}>
          {education.studyType} {education.area}
        </Text>
        <View style={{ flexDirection: 'row', lineHeight }}>
          <Text style={styles.institution}>{education.institution}</Text>
          <Text style={styles.edTenure}>
            <Tenure item={education} />
          </Text>
        </View>
      </View>
    </View>
  )
}

const BulletLine = ({ children }) => {
  const {
    templateStyle: { listLineHeightMultiplier, bodyFontSize },
  } = useStyle()
  const bulletFontSize = (bodyFontSize * 5) / 6
  const styles = StyleSheet.create({
    highlightBullet: {
      padding: '0 4px',
      fontSize: bulletFontSize,
      fontWeight: 'normal',
      color: '#000000',
      lineHeight: `${16 * listLineHeightMultiplier}px`,
    },
    highlightText: {
      fontSize: bulletFontSize,
      fontWeight: 'normal',
      color: '#000000',
      lineHeight: `${16 * listLineHeightMultiplier}px`,
    },
    container: {
      flexDirection: 'row',
      paddingBottom: `${4 * listLineHeightMultiplier}px`,
    },
  })

  const hcbNone = (word) => [word]

  return (
    <View style={styles.container}>
      <Text style={styles.highlightBullet}>&#8226;</Text>
      <Text hyphenationCallback={hcbNone} style={styles.highlightText}>
        {children}
      </Text>
    </View>
  )
}

const momentFormat = (date, nullValue = '') => {
  if (!date || [undefined, null, ''].includes(date)) {
    return nullValue
  }
  try {
    const result = Moment(date).format('MMM YYYY')
    if (result === 'Invalid date') return nullValue
    return result
  } catch {
    return nullValue
  }
}

const isNotBlank = (item) => ![null, undefined, ''].includes(item)

const Tenure = ({ item }) => {
  if (!item.startDate && !item.endDate) {
    return null
  }

  const formattedStart = momentFormat(item.startDate)
  const formattedEnd = momentFormat(item.endDate, 'Present')

  return [formattedStart, formattedEnd].filter(isNotBlank).join(' - ')
}

const EnvelopeIcon = () => {
  return (
    <Svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
    >
      <Path
        d="M7.75109 6.33658L11.3982 8.64221V3.93359L7.75109 6.33658Z"
        fill="black"
      />
      <Path
        d="M0.58 3.93359V8.64221L4.22708 6.33658L0.58 3.93359Z"
        fill="black"
      />
      <Path
        d="M10.722 2.5625H1.25611C0.918718 2.5625 0.650968 2.81402 0.600258 3.13789L5.98906 6.68829L11.3779 3.13789C11.3272 2.81402 11.0594 2.5625 10.722 2.5625Z"
        fill="black"
      />
      <Path
        d="M7.13176 6.74506L6.17502 7.37522C6.11823 7.41241 6.05399 7.43066 5.98908 7.43066C5.92418 7.43066 5.85994 7.41241 5.80315 7.37522L4.84641 6.74438L0.60162 9.42932C0.653682 9.75049 0.920081 9.99998 1.25612 9.99998H10.722C11.0581 9.99998 11.3245 9.75049 11.3766 9.42932L7.13176 6.74506Z"
        fill="black"
      />
    </Svg>
  )
}

const PhoneIcon = () => {
  return (
    <Svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
    >
      <Path
        d="M10.1976 7.56386C9.58537 7.56386 8.98422 7.4681 8.41456 7.27985C8.13542 7.18464 7.79227 7.27198 7.6219 7.44695L6.4975 8.29576C5.19352 7.59969 4.39029 6.79672 3.70372 5.50253L4.52755 4.40742C4.74159 4.19367 4.81836 3.88143 4.72638 3.58846C4.53732 3.01581 4.44128 2.41495 4.44128 1.80245C4.4413 1.35997 4.08133 1 3.63888 1H1.80242C1.35997 1 1 1.35997 1 1.80242C1 6.87404 5.12599 11 10.1976 11C10.6401 11 11 10.64 11 10.1976V8.36625C11 7.92383 10.64 7.56386 10.1976 7.56386Z"
        fill="black"
      />
    </Svg>
  )
}

const LinkedInIcon = () => {
  return (
    <Svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
    >
      <Path
        d="M10.2788 1H1.72121C1.32288 1 1 1.32288 1 1.72121V10.2788C1 10.6771 1.32288 11 1.72121 11H10.2788C10.6771 11 11 10.6771 11 10.2788V1.72121C11 1.32288 10.6771 1 10.2788 1V1ZM4.54706 8.55859H3.32933V4.89503H4.54706V8.55859ZM3.93823 4.39478H3.9303C3.52167 4.39478 3.25739 4.11348 3.25739 3.76192C3.25739 3.40242 3.52975 3.12891 3.94632 3.12891C4.36288 3.12891 4.61923 3.40242 4.62717 3.76192C4.62717 4.11348 4.36288 4.39478 3.93823 4.39478ZM8.93793 8.55859H7.72035V6.59868C7.72035 6.10612 7.54404 5.7702 7.10344 5.7702C6.76706 5.7702 6.56671 5.9968 6.47867 6.21553C6.44647 6.29381 6.43861 6.40321 6.43861 6.5127V8.55859H5.22096C5.22096 8.55859 5.23691 5.23874 5.22096 4.89503H6.43861V5.41376C6.60043 5.16412 6.88997 4.80905 7.53603 4.80905C8.33719 4.80905 8.93793 5.33266 8.93793 6.45792V8.55859Z"
        fill="black"
      />
    </Svg>
  )
}
